import { logger, createLogger } from '@/utils/logger'
import { registerChunkLoadRecovery } from '@/utils/chunkLoadRecovery'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { router } from './router'
import { i18n } from './i18n'
import { useAuthStore } from './stores/auth'
import { usePlayerStore } from './stores/player'
import { BUILD_FEATURES } from './config/build-features'
import App from './App.vue'
import { http } from './utils/http'
import { useLoadingStore } from './stores/loading'
import './style.css'
import contentMenuPlugin from './plugins/contentMenu'
import { intersection } from './directives/intersection'
import { setupMetadataPlugin } from './plugins/metadata'
import * as Sentry from '@sentry/vue'
import { createHead } from '@vueuse/head'
import { initializeTheme } from './utils/theme'

// Create a namespaced logger
const fileLogger = createLogger('main')

// Recover from stale hashed chunks after deploy (before lazy route/service imports run)
registerChunkLoadRecovery()

// Note: Removed epub-js monkey-patch since V2 service uses direct content rendering without iframes

const app = createApp(App)
const pinia = createPinia()
const head = createHead()

app.use(pinia)
app.use(head)

// Create async initialization function
const initializeApp = async () => {
  // Initialize theme colors
  initializeTheme()
  
  const authStore = useAuthStore()
  await authStore.initializeAuth()
  
  // Initialize player store only if authenticated or when needed
  if (BUILD_FEATURES.player) {
    const playerStore = usePlayerStore()
    if (authStore.isAuthenticated) {
      playerStore.initialize()
    } else {
      // For unauthenticated users, only initialize minimal player functionality
      playerStore.initializeBasic()
    }
  }
  
  app.use(router)
  app.use(i18n)
  
  // Initialize stores after Pinia is installed
  const loadingStore = useLoadingStore()

  // Set up http dependencies
  http.setRouter(router)
  http.setLoadingStore(loadingStore)

  app.use(contentMenuPlugin)

  app.directive('intersection', intersection)

  Sentry.init({
    app: app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration({router}),
        // Only enable replay in development
        ...(import.meta.env.DEV ? [Sentry.replayIntegration()] : []),
    ],
    // Performance Monitoring - reduce sampling in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", import.meta.env.VITE_API_URL, import.meta.env.VITE_APP_URL],
  
    // Session Replay - only enable in development
    replaysSessionSampleRate: import.meta.env.DEV ? 0.1 : 0,
    replaysOnErrorSampleRate: import.meta.env.DEV ? 1.0 : 0,
  });

  // Initialize metadata plugin
  setupMetadataPlugin(router)

  // Load sync test helper in development
  if (import.meta.env.DEV) {
    import('@/utils/sync-test-browser').then(module => {
      // Make it available globally for browser console
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window.syncTestHelper = {
          create: module.createSyncTestHelper,
          generateTestBookmark: module.generateTestBookmark,
          generateTestTitle: module.generateTestTitle,
          compareSyncStates: module.compareSyncStates,
          logSyncState: module.logSyncState
        }
      }
    }).catch(() => {
      // Silently fail if module not available
    })
  }

  app.mount('#app')
}

// Call the initialization function
initializeApp().catch(error => {
  fileLogger.error('Failed to initialize app:', error)
})